import Icon from '../../Icon/Icon';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import ReactTooltip from 'react-tooltip';
import { campaignStatus } from '../../../utils/functions';
import PredictionDetails from '../../DashboardIndividual/Predictions/PredictionDetails/PredictionDetails';

const Predictions = ({
    isCampaignPrediction,
    campaignScreensStatus,
    hasPendingOrRejected,
}) => {
    const [showPredictions, setShowPredictions] = useState(true);

    const { t } = useTranslation();
    const { campaign, campaignPrediction } = useSelector(
        (state) => state.dashboard
    );

    const isValidPrediction =
        campaignPrediction && Object.keys(campaignPrediction).length > 0;
    const isActive =
        campaign?.status === campaignStatus('ACTIVE') && isValidPrediction;

    const isDelayed = useMemo(() => {
        if (!campaignPrediction?.endDate) return false;
        return Math.abs(campaignPrediction.endDate) > 1;
    }, [campaignPrediction]);

    // The campaign screens arrive as external ids (or as objects on older
    // payloads) and can be repeated, so they are counted once each.
    const campaignScreensTotal = useMemo(() => {
        const list = Array.isArray(campaign?.screen)
            ? campaign.screen
            : Array.isArray(campaign?.screens)
              ? campaign.screens
              : [];

        const ids = new Set();
        let withoutId = 0;

        list.forEach((item) => {
            const rawId =
                typeof item === 'string' || typeof item === 'number'
                    ? item
                    : (item?.external_id ?? item?.externalId ?? item?.id);
            const id = String(rawId ?? '').trim();
            if (id) ids.add(id.toLowerCase());
            else withoutId += 1;
        });

        return ids.size + withoutId;
    }, [campaign?.screen, campaign?.screens]);

    // A campaign cannot buy on more screens than it has: when the prediction
    // reports more (screens already removed from the campaign, or counted once
    // per deal), show the campaign total and keep the reported value on hover.
    const reportedBuyingScreens = campaignPrediction?.buyingScreen ?? 0;
    const buyingScreens = campaignScreensTotal
        ? Math.min(reportedBuyingScreens, campaignScreensTotal)
        : reportedBuyingScreens;
    const isBuyingScreensClamped = buyingScreens !== reportedBuyingScreens;

    if (!isActive) return null;

    const delayedLabel =
        campaignPrediction.endDate > 1
            ? t('sections.dashboard-individual.predictions.after-schedule')
            : t('sections.dashboard-individual.predictions.before-schedule');

    const delayedDays = Math.abs(campaignPrediction.endDate);

    const pendingScreensIcon = () => {
        if (hasPendingOrRejected) {
            return (
                <Icon
                    onClick={() => setShowPredictions(!showPredictions)}
                    name="exclamation-triangle"
                    fixedWidth
                    className="ml-2 text-yellow-500 cursor-pointer"
                />
            );
        }
    };
    return (
        <div className="flex flex-col h-full">
            {hasPendingOrRejected && (
                <PredictionDetails
                    campaign={campaign}
                    campaignScreensStatus={campaignScreensStatus}
                    setHandleDataSchedule={setShowPredictions}
                    handleDataSchedule={showPredictions}
                    isDelayed={isDelayed}
                    campaignPrediction={
                        hasPendingOrRejected ? campaignPrediction : null
                    }
                />
            )}
            <div className="font-light text-xs uppercase mb-1">
                {t(
                    'sections.campaigns.new-campaign.wizard-screens.screens.screen-currentStatus'
                )}
                {pendingScreensIcon()}
            </div>

            <div className="flex divide-x divide-dotted">
                {/* Screens Info */}
                <div className="pr-2 w-2/6">
                    <span className="text-gray-600 text-xs font-light">
                        {t(
                            'sections.campaigns.new-campaign.wizard-tab.screens'
                        )}
                    </span>
                    <div
                        className="font-extralight text-2xl mt-1"
                        title={
                            isBuyingScreensClamped
                                ? `${reportedBuyingScreens}`
                                : undefined
                        }>
                        <span className="text-black font-semibold">
                            {buyingScreens}
                        </span>
                        /{campaignScreensTotal}
                    </div>
                </div>

                {/* TODO: remove `hidden` when scheduler is re-enabled */}
                <div className="pl-2 w-4/6 hidden">
                    <span className="text-gray-600 text-xs font-light">
                        {t(
                            'sections.dashboard-individual.predictions.estimated-end'
                        )}
                    </span>

                    <div
                        className={`${
                            isDelayed
                                ? 'text-red-600 bg-red-100'
                                : 'text-green-500 bg-gray-50'
                        } mt-2 text-xs rounded-full text-center font-bold`}>
                        {t(
                            `sections.dashboard-individual.predictions.${
                                isDelayed ? 'delayed' : 'on-time'
                            }`
                        )}
                    </div>

                    {isDelayed && (
                        <>
                            <ReactTooltip
                                id="etaTooltip"
                                effect="solid"
                                className="w-48 p-2"
                                aria-haspopup="true">
                                {campaignPrediction.endDate > 0
                                    ? t(
                                          'sections.dashboard-individual.predictions.increase-spending'
                                      )
                                    : t(
                                          'sections.dashboard-individual.predictions.decrease-spending'
                                      )}
                            </ReactTooltip>

                            <div className="text-center mt-2 text-sm">
                                <a
                                    data-tip
                                    data-for="etaTooltip"
                                    className="cursor-pointer">
                                    <Icon
                                        name="exclamation-triangle"
                                        className="text-yellow-400 mr-1"
                                    />
                                    {`${delayedDays} ${delayedLabel}`}
                                </a>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Predictions;
